import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { InfoTip } from "@/components/ui/info-tip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  ChevronLeft, Plus, Minus, Trash2, DoorOpen, RectangleHorizontal,
  Ruler, ZoomIn, ZoomOut, Maximize2, ClipboardList, Copy, Hammer, AlertTriangle,
} from "lucide-react";

// ─── Types ─────────────────────────────────────────────────────────────────────

export type StudSize = "2x4" | "2x6";
export type OpeningType = "door" | "window";

export interface Opening {
  id: string;
  type: OpeningType;
  width: number; // finished opening width (in)
  height: number; // finished opening height (in)
  x: number; // distance from wall left edge to left side of rough opening (in)
  sillHeight?: number; // windows only: floor to bottom of rough opening (in)
}

export interface Plan {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  wallWidth: number;
  wallHeight: number;
  studSpacing: number; // 12 | 16 | 24
  studSize: StudSize;
  openings: Opening[];
}

// ─── Measurement formatting ──────────────────────────────────────────────────

export function formatInches(inches: number, useDecimals = false): string {
  if (useDecimals) return `${Math.round(inches * 100) / 100}"`;
  const neg = inches < 0;
  const v = Math.abs(inches);
  // Work in integer sixteenths so a value like 11.97 rounds cleanly to 12".
  const sixteenths = Math.round(v * 16);
  const wholeInches = Math.floor(sixteenths / 16);
  const frac = sixteenths - wholeInches * 16;
  const feet = Math.floor(wholeInches / 12);
  const inch = wholeInches - feet * 12;

  let fracStr = "";
  if (frac > 0) {
    let num = frac;
    let den = 16;
    while (num % 2 === 0) { num /= 2; den /= 2; }
    fracStr = `${num}/${den}`;
  }

  const sign = neg ? "-" : "";
  const inchPart = `${inch}${fracStr ? ` ${fracStr}` : ""}`;

  if (feet > 0) {
    if (inch === 0 && !fracStr) return `${sign}${feet}'`;
    return `${sign}${feet}' ${inchPart}"`;
  }
  return `${sign}${inchPart}"`;
}

/** Whole inches and fractions, for sizes like 82 1/2" where feet read badly. */
function plainInches(inches: number): string {
  const sixteenths = Math.round(inches * 16);
  const whole = Math.floor(sixteenths / 16);
  let num = sixteenths - whole * 16;
  if (!num) return `${whole}"`;
  let den = 16;
  while (num % 2 === 0) { num /= 2; den /= 2; }
  return `${whole ? `${whole} ` : ""}${num}/${den}"`;
}

function uid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ─── Framing math ─────────────────────────────────────────────────────────────

const T = 1.5; // lumber thickness as seen in elevation
const PLATE = 1.5; // plate height

type PieceType = "plate" | "stud" | "header" | "cripple" | "king" | "jack" | "sill";

export interface FramingPiece {
  id: string;
  type: PieceType;
  x: number;
  y: number;
  w: number;
  h: number;
  length: number;
  size?: string;
  plies?: number;
}

/** Rough-and-ready header sizing from clear span. Verify against local code. */
function headerForSpan(roWidth: number): { label: string; height: number } {
  const ft = roWidth / 12;
  if (ft <= 3) return { label: "2x6", height: 5.5 };
  if (ft <= 5) return { label: "2x8", height: 7.25 };
  if (ft <= 7) return { label: "2x10", height: 9.25 };
  return { label: "2x12", height: 11.25 };
}

/** An opening's rough size and header, as framed by calculateFraming. */
function openingSize(op: Opening) {
  const isDoor = op.type === "door";
  const roW = op.width + 2;
  const roH = isDoor ? op.height + 2.5 : op.height + 2;
  const bottom = isDoor ? 0 : Math.max(PLATE * 2, op.sillHeight ?? 0);
  const jackQty = roW > 72 ? 2 : 1;
  return { roW, roH, bottom, jackQty, headerLabel: headerForSpan(roW).label, headerLength: roW + jackQty * T * 2 };
}

export function calculateFraming(plan: Plan): FramingPiece[] {
  const pieces: FramingPiece[] = [];
  const W = plan.wallWidth;
  const H = plan.wallHeight;
  const studHeight = Math.max(0, H - PLATE * 3); // bottom + double top plate

  // Bottom plate
  pieces.push({ id: "bp", type: "plate", x: 0, y: 0, w: W, h: PLATE, length: W });
  // Double top plate
  pieces.push({ id: "tp1", type: "plate", x: 0, y: H - PLATE * 2, w: W, h: PLATE, length: W });
  pieces.push({ id: "tp2", type: "plate", x: 0, y: H - PLATE, w: W, h: PLATE, length: W });

  // Stud layout positions
  const layout: number[] = [];
  for (let x = 0; x <= W - T + 0.001; x += plan.studSpacing) layout.push(x);
  if (layout.length === 0 || Math.abs(layout[layout.length - 1] - (W - T)) > 0.01) {
    layout.push(W - T);
  }

  const takenXs: number[] = []; // kings and jacks, which a layout stud can't overlap
  const regions: {
    type: OpeningType;
    inStart: number; // left edge of rough opening
    inEnd: number; // right edge of rough opening
    bottom: number; // floor to bottom of rough opening (0 for a door)
    headerTop: number;
  }[] = [];

  plan.openings.forEach((op, i) => {
    // A door's rough opening starts at the floor (the plate is cut out after the
    // wall stands). A window's sill height is from the floor, above plate + sill.
    const { roW, roH, bottom, jackQty, headerLength } = openingSize(op);
    const roTop = bottom + roH;
    const hdr = headerForSpan(roW);

    // The rough opening is the clear space between the jacks, so the jacks and
    // kings sit outside it and the header runs across the jacks.
    const startX = op.x;
    const endX = startX + roW;
    const headerX = startX - jackQty * T;
    const kingL = headerX - T;
    const kingR = headerX + headerLength;
    takenXs.push(kingL, kingR);
    regions.push({ type: op.type, inStart: startX, inEnd: endX, bottom, headerTop: roTop + hdr.height });

    // King studs (full height)
    pieces.push({ id: `kl${i}`, type: "king", x: kingL, y: PLATE, w: T, h: studHeight, length: studHeight });
    pieces.push({ id: `kr${i}`, type: "king", x: kingR, y: PLATE, w: T, h: studHeight, length: studHeight });

    // Jack / trimmer studs (support the header)
    const jackH = roTop - PLATE;
    for (let j = 0; j < jackQty; j++) {
      const jackL = startX - (j + 1) * T;
      const jackR = endX + j * T;
      takenXs.push(jackL, jackR);
      pieces.push({ id: `jl${i}-${j}`, type: "jack", x: jackL, y: PLATE, w: T, h: jackH, length: jackH });
      pieces.push({ id: `jr${i}-${j}`, type: "jack", x: jackR, y: PLATE, w: T, h: jackH, length: jackH });
    }

    // Header (2-ply), spans king to king, rests on the jacks
    pieces.push({
      id: `h${i}`, type: "header", x: headerX, y: roTop, w: headerLength, h: hdr.height,
      length: headerLength, size: hdr.label, plies: 2,
    });

    // Rough sill under a window
    if (op.type !== "door") {
      pieces.push({ id: `sl${i}`, type: "sill", x: startX, y: bottom - PLATE, w: roW, h: PLATE, length: roW });
    }
  });

  // Common studs + cripples
  layout.forEach((x, i) => {
    // Skip if it collides with a king or jack
    if (takenXs.some((m) => Math.abs(x - m) < T)) return;

    // Inside an opening?
    const reg = regions.find((r) => x > r.inStart - T && x < r.inEnd);
    if (reg) {
      // Cripple above the header
      const topH = (H - PLATE * 2) - reg.headerTop;
      if (topH > 0.5) {
        pieces.push({ id: `ct${i}`, type: "cripple", x, y: reg.headerTop, w: T, h: topH, length: topH });
      }
      // Cripple below the sill (windows)
      if (reg.type === "window") {
        const botH = reg.bottom - PLATE * 2; // subtract bottom plate and rough sill
        if (botH > 0.5) {
          pieces.push({ id: `cb${i}`, type: "cripple", x, y: PLATE, w: T, h: botH, length: botH });
        }
      }
      return;
    }

    pieces.push({ id: `s${i}`, type: "stud", x, y: PLATE, w: T, h: studHeight, length: studHeight });
  });

  return pieces;
}

/** Openings that can't be framed as entered: off the wall, overlapping or too tall. */
export function framingWarnings(plan: Plan): string[] {
  const warnings: string[] = [];
  const spans = plan.openings.map((op, i) => {
    const isDoor = op.type === "door";
    const roW = op.width + 2;
    const sides = (roW > 72 ? 2 : 1) + 1; // jacks + king each side
    const name = `${isDoor ? "Door" : "Window"} ${i + 1}`;
    if (op.x - sides * T < 0) warnings.push(`${name} is too close to the left end of the wall for its jack and king studs`);
    if (op.x + roW + sides * T > plan.wallWidth) warnings.push(`${name} runs past the right end of the wall`);
    const roTop = isDoor ? op.height + 2.5 : Math.max(PLATE * 2, op.sillHeight ?? 0) + op.height + 2;
    if (roTop + headerForSpan(roW).height > plan.wallHeight - PLATE * 2) warnings.push(`${name} and its header don't fit under the top plates`);
    return { name, start: op.x - sides * T, end: op.x + roW + sides * T };
  });
  spans.forEach((a, i) => {
    spans.slice(i + 1).forEach((b) => {
      if (a.start < b.end && b.start < a.end) warnings.push(`${a.name} and ${b.name.toLowerCase()} overlap`);
    });
  });
  return warnings;
}

export interface CutPiece {
  id: string;
  label: string;
  length: number;
  qty: number;
}

export interface Cutlist {
  plates: CutPiece[];
  studs: CutPiece[];
  kings: CutPiece[];
  jacks: CutPiece[];
  headers: CutPiece[];
  cripples: CutPiece[];
  sills: CutPiece[];
}

export function generateCutlist(plan: Plan): Cutlist {
  const pieces = calculateFraming(plan);
  const out: Cutlist = { plates: [], studs: [], kings: [], jacks: [], headers: [], cripples: [], sills: [] };

  const add = (arr: CutPiece[], label: string, length: number, qty = 1) => {
    const existing = arr.find((p) => p.label === label && Math.abs(p.length - length) < 0.1);
    if (existing) existing.qty += qty;
    else arr.push({ id: uid(), label, length, qty });
  };

  pieces.forEach((p) => {
    if (p.type === "plate") add(out.plates, "Plate", p.length);
    else if (p.type === "stud") add(out.studs, `Common Stud (${plan.studSize})`, p.length);
    else if (p.type === "king") add(out.kings, `King Stud (${plan.studSize})`, p.length);
    else if (p.type === "jack") add(out.jacks, `Jack Stud (${plan.studSize})`, p.length);
    else if (p.type === "cripple") add(out.cripples, `Cripple (${plan.studSize})`, p.length);
    else if (p.type === "sill") add(out.sills, `Rough Sill (${plan.studSize})`, p.length);
    else if (p.type === "header") add(out.headers, `Header (${p.size})`, p.length, p.plies ?? 1);
  });

  return out;
}

// ─── localStorage store ───────────────────────────────────────────────────────

const STORAGE_KEY = "quotrak:wall-plans";
const PLANS_EVENT = "quotrak-wall-plans-updated";

function readPlans(): Plan[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Plan[]) : [];
  } catch {
    return [];
  }
}

function writePlans(plans: Plan[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(plans));
  window.dispatchEvent(new Event(PLANS_EVENT));
}

function useWallPlans() {
  const [plans, setPlans] = useState<Plan[]>(readPlans);

  useEffect(() => {
    const handler = () => setPlans(readPlans());
    window.addEventListener(PLANS_EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(PLANS_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const addPlan = useCallback((init: Omit<Plan, "id" | "createdAt" | "updatedAt">) => {
    const now = Date.now();
    const plan: Plan = { ...init, id: uid(), createdAt: now, updatedAt: now };
    writePlans([...readPlans(), plan]);
    return plan.id;
  }, []);

  const updatePlan = useCallback((id: string, updates: Partial<Omit<Plan, "id" | "createdAt" | "updatedAt">>) => {
    writePlans(readPlans().map((p) => (p.id === id ? { ...p, ...updates, updatedAt: Date.now() } : p)));
  }, []);

  const deletePlan = useCallback((id: string) => {
    writePlans(readPlans().filter((p) => p.id !== id));
  }, []);

  return { plans, addPlan, updatePlan, deletePlan };
}

// ─── Dimension input (ft / in steppers) ──────────────────────────────────────

function DimInput({
  label, valueInches, onChange, min = 0, max = 9999,
}: {
  label: string;
  valueInches: number;
  onChange: (inches: number) => void;
  min?: number;
  max?: number;
}) {
  const [feetStr, setFeetStr] = useState(String(Math.floor(valueInches / 12)));
  const [inchStr, setInchStr] = useState(String(Math.round((valueInches % 12) * 100) / 100));

  useEffect(() => {
    setFeetStr(String(Math.floor(valueInches / 12)));
    setInchStr(String(Math.round((valueInches % 12) * 100) / 100));
  }, [valueInches]);

  const clamp = (v: number) => Math.max(min, Math.min(max, v));

  const commit = (f: string, inc: string) => {
    const total = (parseFloat(f) || 0) * 12 + (parseFloat(inc) || 0);
    onChange(clamp(total));
  };

  const nudge = (dir: 1 | -1) => onChange(clamp(valueInches + dir));

  const cellStyle: React.CSSProperties = {
    height: "3rem", fontSize: "1.0625rem", fontWeight: 600,
    textAlign: "center", fontVariantNumeric: "tabular-nums",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
      <label style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))" }}>
        {label}
      </label>
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        <button
          onClick={() => nudge(-1)}
          aria-label="Decrease"
          style={iconBtnStyle}
          className="hover:bg-accent transition-colors"
        >
          <Minus style={{ width: "1.125rem", height: "1.125rem" }} />
        </button>
        <div style={{ flex: 1, display: "flex", gap: "0.4rem" }}>
          <div style={{ position: "relative", flex: 1 }}>
            <Input
              type="number" inputMode="decimal" value={feetStr}
              onChange={(e) => { setFeetStr(e.target.value); commit(e.target.value, inchStr); }}
              style={cellStyle}
            />
            <span style={unitLabelStyle}>ft</span>
          </div>
          <div style={{ position: "relative", flex: 1 }}>
            <Input
              type="number" inputMode="decimal" value={inchStr}
              onChange={(e) => { setInchStr(e.target.value); commit(feetStr, e.target.value); }}
              style={cellStyle}
            />
            <span style={unitLabelStyle}>in</span>
          </div>
        </div>
        <button
          onClick={() => nudge(1)}
          aria-label="Increase"
          style={iconBtnStyle}
          className="hover:bg-accent transition-colors"
        >
          <Plus style={{ width: "1.125rem", height: "1.125rem" }} />
        </button>
      </div>
    </div>
  );
}

const iconBtnStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "center",
  width: "3rem", height: "3rem", flexShrink: 0,
  border: "1px solid hsl(var(--border))", borderRadius: "0.5rem",
  backgroundColor: "hsl(var(--card))", cursor: "pointer",
  color: "hsl(var(--foreground))",
};

const unitLabelStyle: React.CSSProperties = {
  position: "absolute", right: "0.6rem", top: "50%", transform: "translateY(-50%)",
  fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", pointerEvents: "none",
};

// ─── Wall elevation preview ──────────────────────────────────────────────────

const MEMBER_COLORS: Record<PieceType, string> = {
  plate: "#b45309",
  stud: "#ca8a04",
  king: "#92400e",
  jack: "#a16207",
  header: "#7c2d12",
  cripple: "#eab308",
  sill: "#57534e",
};

const LEGEND: { type: PieceType; label: string }[] = [
  { type: "plate", label: "Plates" },
  { type: "stud", label: "Studs" },
  { type: "king", label: "Kings" },
  { type: "jack", label: "Jacks" },
  { type: "header", label: "Headers" },
  { type: "cripple", label: "Cripples" },
  { type: "sill", label: "Rough Sills" },
];

function WallPreview({ plan }: { plan: Plan }) {
  const pieces = useMemo(() => calculateFraming(plan), [plan]);
  const [zoom, setZoom] = useState(1);

  const pad = 28;
  const vbW = plan.wallWidth + pad * 2;
  const vbH = plan.wallHeight + pad * 2;
  const fontSize = Math.max(7, plan.wallHeight * 0.06);
  const tagSize = Math.max(3.2, Math.max(plan.wallWidth, plan.wallHeight) * 0.03);

  return (
    <div>
      <div
        style={{
          position: "relative", borderRadius: "0.625rem", overflow: "hidden",
          border: "1px solid hsl(var(--border))", backgroundColor: "#f8f7f4",
        }}
      >
        <div style={{ position: "absolute", top: "0.5rem", left: "0.5rem", zIndex: 2, fontSize: "0.625rem", fontWeight: 700, letterSpacing: "0.05em", color: "#57534e", backgroundColor: "rgba(255,255,255,0.8)", padding: "0.15rem 0.4rem", borderRadius: "0.3rem" }}>
          ELEVATION
        </div>

        {/* Zoom controls */}
        <div style={{ position: "absolute", top: "0.5rem", right: "0.5rem", zIndex: 2, display: "flex", gap: "0.25rem" }}>
          <button onClick={() => setZoom((z) => Math.max(1, Math.round((z - 0.5) * 10) / 10))} aria-label="Zoom out" style={zoomBtnStyle}>
            <ZoomOut style={{ width: "1rem", height: "1rem" }} />
          </button>
          <button onClick={() => setZoom(1)} aria-label="Fit" style={zoomBtnStyle}>
            <Maximize2 style={{ width: "1rem", height: "1rem" }} />
          </button>
          <button onClick={() => setZoom((z) => Math.min(4, Math.round((z + 0.5) * 10) / 10))} aria-label="Zoom in" style={zoomBtnStyle}>
            <ZoomIn style={{ width: "1rem", height: "1rem" }} />
          </button>
        </div>

        <div style={{ width: "100%", aspectRatio: "4 / 3", overflow: "auto", WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"] }}>
          <div style={{ width: `${zoom * 100}%`, height: `${zoom * 100}%`, minWidth: "100%", minHeight: "100%" }}>
            <svg viewBox={`${-pad} ${-pad} ${vbW} ${vbH}`} width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
              <defs>
                <pattern id="wf-grid" width="12" height="12" patternUnits="userSpaceOnUse">
                  <path d="M 12 0 L 0 0 0 12" fill="none" stroke="#00000010" strokeWidth="0.4" />
                </pattern>
              </defs>
              <rect x={-pad} y={-pad} width={vbW} height={vbH} fill="url(#wf-grid)" />

              {/* Framing (flip Y so it draws bottom-up) */}
              <g transform={`translate(0, ${plan.wallHeight}) scale(1, -1)`}>
                {pieces.map((p) => (
                  <rect
                    key={p.id} x={p.x} y={p.y} width={p.w} height={p.h}
                    fill={MEMBER_COLORS[p.type]} stroke="#ffffff" strokeWidth="0.4" rx="0.4"
                  />
                ))}
              </g>

              {/* Opening numbers, keyed to the list under the drawing */}
              {plan.openings.map((op, i) => {
                const { roW, roH, bottom } = openingSize(op);
                const cx = op.x + roW / 2;
                const cy = plan.wallHeight - (bottom + roH / 2);
                return (
                  <g key={op.id}>
                    <circle cx={cx} cy={cy} r={tagSize * 0.9} fill="#44403c" />
                    <text x={cx} y={cy + tagSize * 0.38} textAnchor="middle" fontSize={tagSize} fontWeight={700} fill="#ffffff">{i + 1}</text>
                  </g>
                );
              })}

              {/* Dimension labels (screen-oriented) */}
              <text x={plan.wallWidth / 2} y={plan.wallHeight + pad * 0.75} textAnchor="middle" fontSize={fontSize} fontWeight={700} fill="#44403c">
                {formatInches(plan.wallWidth)}
              </text>
              <text x={-pad * 0.65} y={plan.wallHeight / 2} textAnchor="middle" fontSize={fontSize} fontWeight={700} fill="#44403c" transform={`rotate(-90, ${-pad * 0.65}, ${plan.wallHeight / 2})`}>
                {formatInches(plan.wallHeight)}
              </text>
            </svg>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem 0.875rem", marginTop: "0.625rem" }}>
        {LEGEND.map((l) => (
          <div key={l.type} style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>
            <span style={{ width: "0.7rem", height: "0.7rem", borderRadius: "0.15rem", backgroundColor: MEMBER_COLORS[l.type], flexShrink: 0 }} />
            {l.label}
          </div>
        ))}
      </div>

      <OpeningList plan={plan} />
    </div>
  );
}

/** Door or window size, rough opening and header for each opening, always shown. */
function OpeningList({ plan }: { plan: Plan }) {
  if (plan.openings.length === 0) return null;
  return (
    <ul style={{ listStyle: "none", margin: "0.625rem 0 0", padding: 0 }}>
      {plan.openings.map((op, i) => {
        const isDoor = op.type === "door";
        const { roW, roH, headerLabel, headerLength } = openingSize(op);
        const rows = [
          [isDoor ? "Door" : "Window", `${plainInches(op.width)} × ${plainInches(op.height)}`],
          ["Rough opening", `${plainInches(roW)} × ${plainInches(roH)}`],
          ["Header", `${headerLabel} × ${plainInches(headerLength)}`],
        ];
        return (
          <li key={op.id} style={{ padding: "0.5rem 0", borderTop: "1px solid hsl(var(--border))" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.8125rem", fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: "0.25rem" }}>
              <span style={{ width: "1.125rem", height: "1.125rem", borderRadius: "50%", backgroundColor: "#44403c", color: "#ffffff", fontSize: "0.6875rem", lineHeight: "1.125rem", textAlign: "center", flexShrink: 0 }}>{i + 1}</span>
              {isDoor ? "Door" : "Window"} {i + 1}
            </div>
            <dl style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(6.5rem, 1fr))", gap: "0.25rem 0.75rem", margin: 0 }}>
              {rows.map(([label, value]) => (
                <div key={label}>
                  <dt style={{ fontSize: "0.6875rem", color: "hsl(var(--muted-foreground))" }}>{label}</dt>
                  <dd style={{ margin: 0, fontSize: "0.875rem", fontWeight: 600, fontVariantNumeric: "tabular-nums", color: "hsl(var(--foreground))" }}>{value}</dd>
                </div>
              ))}
            </dl>
          </li>
        );
      })}
    </ul>
  );
}

const zoomBtnStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "center",
  width: "1.875rem", height: "1.875rem", borderRadius: "0.375rem",
  border: "1px solid rgba(0,0,0,0.1)", backgroundColor: "rgba(255,255,255,0.9)",
  cursor: "pointer", color: "#44403c",
};

// ─── Material summary ─────────────────────────────────────────────────────────

function MaterialSummary({ plan }: { plan: Plan }) {
  const cutlist = useMemo(() => generateCutlist(plan), [plan]);

  const totalStuds =
    sumQty(cutlist.studs) + sumQty(cutlist.kings) + sumQty(cutlist.jacks);
  const plateRunBoards = Math.ceil(plan.wallWidth / 192) * 3; // 3 plate runs, 16' stock
  const headerCount = sumQty(cutlist.headers);

  const tiles: { value: number; label: string; tip: string }[] = [
    { value: totalStuds, label: `Studs (${plan.studSize})`, tip: "Common studs at your spacing + king and jack studs framing each opening." },
    { value: plateRunBoards, label: "16' Plates", tip: "Three plate runs (bottom + double top) ÷ 16′ stock length, rounded up: ⌈wall width ÷ 16′⌉ × 3." },
    { value: headerCount + sumQty(cutlist.cripples), label: "Hdr + Cripples", tip: "One header per opening plus the short cripple studs above (and below sills) that fill out the stud spacing." },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem" }}>
      {tiles.map((t, i) => (
        <div key={i} style={{ backgroundColor: "hsl(var(--muted))", borderRadius: "0.5rem", padding: "0.75rem 0.5rem", textAlign: "center" }}>
          <div style={{ fontSize: "1.5rem", fontWeight: 700, fontVariantNumeric: "tabular-nums", color: "hsl(var(--foreground))", lineHeight: 1.1 }}>{t.value}</div>
          <div style={{ fontSize: "0.6875rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.03em", color: "hsl(var(--muted-foreground))", marginTop: "0.25rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.25rem" }}>
            {t.label}
            <InfoTip content={t.tip} />
          </div>
        </div>
      ))}
    </div>
  );
}

function sumQty(arr: CutPiece[]): number {
  return arr.reduce((s, p) => s + p.qty, 0);
}

// ─── Full cutlist ─────────────────────────────────────────────────────────────

function cutlistToText(plan: Plan, cutlist: Cutlist): string {
  const lines: string[] = [];
  lines.push(`${plan.name} — Cutlist`);
  lines.push(`Wall: ${formatInches(plan.wallWidth)} x ${formatInches(plan.wallHeight)}  |  ${plan.studSpacing}" OC  |  ${plan.studSize}`);
  lines.push("");
  const sections: [string, CutPiece[]][] = [
    ["Plates", cutlist.plates],
    ["Common Studs", cutlist.studs],
    ["King Studs", cutlist.kings],
    ["Jack Studs", cutlist.jacks],
    ["Headers", cutlist.headers],
    ["Rough Sills", cutlist.sills],
    ["Cripples", cutlist.cripples],
  ];
  for (const [title, items] of sections) {
    if (!items.length) continue;
    lines.push(`${title.toUpperCase()}`);
    [...items].sort((a, b) => b.length - a.length).forEach((it) => {
      lines.push(`  ${it.qty}x @ ${formatInches(it.length)}`);
    });
    lines.push("");
  }
  lines.push("");
  lines.push("For reference only. Check your local code before you build.");
  return lines.join("\n").trim();
}

function CutlistView({ plan, onBack }: { plan: Plan; onBack: () => void }) {
  const { toast } = useToast();
  const cutlist = useMemo(() => generateCutlist(plan), [plan]);

  const sections: [string, CutPiece[]][] = [
    ["Plates", cutlist.plates],
    ["Common Studs", cutlist.studs],
    ["King Studs", cutlist.kings],
    ["Jack Studs", cutlist.jacks],
    ["Headers", cutlist.headers],
    ["Rough Sills", cutlist.sills],
    ["Cripples", cutlist.cripples],
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(cutlistToText(plan, cutlist));
      toast({ title: "Cutlist copied" });
    } catch {
      toast({ title: "Couldn't copy", variant: "destructive" });
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={onBack} style={backBtnStyle} className="hover:text-foreground transition-colors">
          <ChevronLeft style={{ width: "1.125rem", height: "1.125rem" }} />
          Editor
        </button>
        <Button size="sm" variant="outline" onClick={copy} style={{ height: "2.25rem" }}>
          <Copy style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.4rem" }} />
          Copy
        </Button>
      </div>

      <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", fontVariantNumeric: "tabular-nums" }}>
        {formatInches(plan.wallWidth)} × {formatInches(plan.wallHeight)} · {plan.studSpacing}" OC · {plan.studSize}
      </div>

      {sections.every(([, items]) => items.length === 0) ? (
        <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>Nothing to cut yet.</p>
      ) : (
        sections.map(([title, items]) =>
          items.length === 0 ? null : (
            <div key={title}>
              <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))", borderBottom: "1px solid hsl(var(--border))", paddingBottom: "0.3rem", marginBottom: "0.4rem" }}>
                {title}
              </div>
              <ul style={{ display: "flex", flexDirection: "column", gap: "0.25rem", listStyle: "none", margin: 0, padding: 0 }}>
                {[...items].sort((a, b) => b.length - a.length).map((it) => (
                  <li key={it.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.4rem 0.625rem", backgroundColor: "hsl(var(--muted))", borderRadius: "0.4rem", fontVariantNumeric: "tabular-nums" }}>
                    <span style={{ fontWeight: 700, fontSize: "0.9375rem" }}>{it.qty}×</span>
                    <span style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", flex: 1, textAlign: "center" }}>{it.label}</span>
                    <span style={{ fontWeight: 600, fontSize: "0.9375rem" }}>@ {formatInches(it.length)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )
        )
      )}

      <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", fontStyle: "italic", lineHeight: 1.5, marginTop: "0.25rem" }}>
        <strong>For reference only.</strong> Header sizes are a rule of thumb. Check them against your local code, and use a structural engineer for load-bearing walls.
      </p>
    </div>
  );
}

const backBtnStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: "0.375rem",
  fontSize: "0.9375rem", color: "hsl(var(--muted-foreground))",
  background: "none", border: "none", cursor: "pointer", padding: "0.25rem 0",
};

// ─── Plan editor ──────────────────────────────────────────────────────────────

function PlanEditor({
  plan, onBack, onUpdate, onDelete,
}: {
  plan: Plan;
  onBack: () => void;
  onUpdate: (updates: Partial<Omit<Plan, "id" | "createdAt" | "updatedAt">>) => void;
  onDelete: () => void;
}) {
  const [tab, setTab] = useState<"dims" | "openings" | "config">("dims");
  const [showCutlist, setShowCutlist] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const warnings = useMemo(() => framingWarnings(plan), [plan]);

  const addOpening = (type: OpeningType) => {
    const newOp: Opening = {
      id: uid(), type,
      width: type === "door" ? 36 : 48,
      height: type === "door" ? 80 : 48,
      x: Math.max(T * 2, Math.round(plan.wallWidth / 2 - 18)),
      sillHeight: type === "window" ? 30 : undefined,
    };
    onUpdate({ openings: [...plan.openings, newOp] });
  };

  const updateOpening = (id: string, updates: Partial<Opening>) => {
    onUpdate({ openings: plan.openings.map((o) => (o.id === id ? { ...o, ...updates } : o)) });
  };

  const removeOpening = (id: string) => {
    onUpdate({ openings: plan.openings.filter((o) => o.id !== id) });
  };

  if (showCutlist) {
    return <CutlistView plan={plan} onBack={() => setShowCutlist(false)} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <button onClick={onBack} style={backBtnStyle} className="hover:text-foreground transition-colors">
          <ChevronLeft style={{ width: "1.125rem", height: "1.125rem" }} />
        </button>
        <Input
          value={plan.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          placeholder="Wall name"
          style={{ fontSize: "1.0625rem", fontWeight: 600, height: "2.75rem", flex: 1 }}
        />
      </div>

      <WallPreview plan={plan} />
      <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", fontStyle: "italic", lineHeight: 1.5, margin: 0 }}>
        <strong>For reference only.</strong> Check your local code before you build.
      </p>

      {warnings.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", padding: "0.625rem 0.875rem", borderRadius: "0.5rem", backgroundColor: "#fffbeb", border: "1px solid #fde68a" }}>
          {warnings.map((w) => (
            <div key={w} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <AlertTriangle style={{ width: "1rem", height: "1rem", color: "#d97706", flexShrink: 0 }} />
              <span style={{ fontSize: "0.8125rem", color: "#92400e" }}>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* Sub-tabs */}
      <div style={{ display: "flex", backgroundColor: "hsl(var(--muted))", padding: "0.25rem", borderRadius: "0.5rem", gap: "0.25rem" }}>
        {([["dims", "Dimensions"], ["openings", "Openings"], ["config", "Config"]] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              flex: 1, padding: "0.5rem", fontSize: "0.8125rem", fontWeight: 600,
              borderRadius: "0.375rem", border: "none", cursor: "pointer",
              backgroundColor: tab === key ? "hsl(var(--background))" : "transparent",
              color: tab === key ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
              boxShadow: tab === key ? "var(--shadow-xs)" : "none",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "dims" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <DimInput label="Wall Width" valueInches={plan.wallWidth} onChange={(v) => onUpdate({ wallWidth: v })} min={T * 2} max={2400} />
          <DimInput label="Wall Height" valueInches={plan.wallHeight} onChange={(v) => onUpdate({ wallHeight: v })} min={PLATE * 4} max={240} />
          <div style={{ borderTop: "1px solid hsl(var(--border))", paddingTop: "1rem" }}>
            <MaterialSummary plan={plan} />
            <Button onClick={() => setShowCutlist(true)} variant="outline" className="w-full" style={{ height: "2.75rem", marginTop: "0.75rem" }}>
              <ClipboardList style={{ width: "1rem", height: "1rem", marginRight: "0.5rem" }} />
              Full Cutlist
            </Button>
          </div>
        </div>
      )}

      {tab === "openings" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button onClick={() => addOpening("door")} variant="outline" className="flex-1" style={{ height: "2.75rem" }}>
              <DoorOpen style={{ width: "1rem", height: "1rem", marginRight: "0.4rem" }} /> Door
            </Button>
            <Button onClick={() => addOpening("window")} variant="outline" className="flex-1" style={{ height: "2.75rem" }}>
              <RectangleHorizontal style={{ width: "1rem", height: "1rem", marginRight: "0.4rem" }} /> Window
            </Button>
          </div>

          {plan.openings.length === 0 ? (
            <p style={{ textAlign: "center", padding: "1.5rem 0", color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>
              No doors or windows yet.
            </p>
          ) : (
            plan.openings.map((op, i) => (
              <div key={op.id} style={{ border: "1px solid hsl(var(--border))", borderRadius: "0.625rem", padding: "0.875rem", display: "flex", flexDirection: "column", gap: "0.875rem" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "0.8125rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--foreground))", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    {op.type === "door" ? <DoorOpen style={{ width: "0.9rem", height: "0.9rem" }} /> : <RectangleHorizontal style={{ width: "0.9rem", height: "0.9rem" }} />}
                    {op.type} {i + 1}
                  </span>
                  <button onClick={() => removeOpening(op.id)} aria-label="Remove" style={{ background: "none", border: "none", cursor: "pointer", color: "hsl(var(--destructive))", padding: "0.25rem" }}>
                    <Trash2 style={{ width: "1rem", height: "1rem" }} />
                  </button>
                </div>
                <DimInput label={`${op.type === "door" ? "Door" : "Window"} Width`} valueInches={op.width} onChange={(v) => updateOpening(op.id, { width: v })} min={1} max={plan.wallWidth} />
                <DimInput label={`${op.type === "door" ? "Door" : "Window"} Height`} valueInches={op.height} onChange={(v) => updateOpening(op.id, { height: v })} min={1} max={plan.wallHeight} />
                {op.type === "window" && (
                  <DimInput label="Sill Height (from floor)" valueInches={op.sillHeight ?? 0} onChange={(v) => updateOpening(op.id, { sillHeight: v })} min={0} max={plan.wallHeight} />
                )}
                <DimInput label="Distance From Left" valueInches={op.x} onChange={(v) => updateOpening(op.id, { x: v })} min={0} max={plan.wallWidth} />
              </div>
            ))
          )}
        </div>
      )}

      {tab === "config" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          <div>
            <label style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))", display: "block", marginBottom: "0.5rem" }}>
              Stud Spacing
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem" }}>
              {[12, 16, 24].map((s) => (
                <Button key={s} variant={plan.studSpacing === s ? "default" : "outline"} style={{ height: "2.75rem" }} onClick={() => onUpdate({ studSpacing: s })}>
                  {s}" OC
                </Button>
              ))}
            </div>
          </div>
          <div>
            <label style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))", display: "block", marginBottom: "0.5rem" }}>
              Stud Size
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
              {(["2x4", "2x6"] as StudSize[]).map((s) => (
                <Button key={s} variant={plan.studSize === s ? "default" : "outline"} style={{ height: "2.75rem" }} onClick={() => onUpdate({ studSize: s })}>
                  {s}
                </Button>
              ))}
            </div>
          </div>

          <div style={{ borderTop: "1px solid hsl(var(--border))", paddingTop: "1rem" }}>
            {confirmDelete ? (
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ fontSize: "0.8125rem", flex: 1, color: "hsl(var(--muted-foreground))" }}>Delete this plan?</span>
                <Button size="sm" variant="destructive" style={{ height: "2.25rem" }} onClick={onDelete}>Delete</Button>
                <Button size="sm" variant="ghost" style={{ height: "2.25rem" }} onClick={() => setConfirmDelete(false)}>Cancel</Button>
              </div>
            ) : (
              <Button variant="outline" className="w-full" style={{ height: "2.75rem", color: "hsl(var(--destructive))" }} onClick={() => setConfirmDelete(true)}>
                <Trash2 style={{ width: "1rem", height: "1rem", marginRight: "0.5rem" }} />
                Delete Plan
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Top-level: list + editor ─────────────────────────────────────────────────

function fmtDate(ts: number) {
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function WallFramer() {
  const { plans, addPlan, updatePlan, deletePlan } = useWallPlans();
  const [editingId, setEditingId] = useState<string | null>(null);

  const editing = editingId ? plans.find((p) => p.id === editingId) ?? null : null;
  // Guard against an editingId whose plan was deleted elsewhere.
  const prevEditing = useRef(editingId);
  useEffect(() => {
    if (editingId && !plans.some((p) => p.id === editingId) && prevEditing.current === editingId) {
      setEditingId(null);
    }
    prevEditing.current = editingId;
  }, [editingId, plans]);

  const handleNew = () => {
    const id = addPlan({
      name: `Wall ${plans.length + 1}`,
      wallWidth: 144,
      wallHeight: 96,
      studSpacing: 16,
      studSize: "2x4",
      openings: [],
    });
    setEditingId(id);
  };

  if (editing) {
    return (
      <PlanEditor
        plan={editing}
        onBack={() => setEditingId(null)}
        onUpdate={(u) => updatePlan(editing.id, u)}
        onDelete={() => { deletePlan(editing.id); setEditingId(null); }}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <Button onClick={handleNew} className="w-full" style={{ height: "3rem" }}>
        <Plus style={{ width: "1rem", height: "1rem", marginRight: "0.5rem" }} />
        New Wall Plan
      </Button>

      {plans.length === 0 ? (
        <div style={{ textAlign: "center", padding: "2.5rem 1rem", color: "hsl(var(--muted-foreground))" }}>
          <Hammer style={{ width: "2rem", height: "2rem", margin: "0 auto 0.5rem", opacity: 0.3 }} />
          <p style={{ fontSize: "0.875rem" }}>No wall plans yet. Tap New Wall Plan to start.</p>
        </div>
      ) : (
        [...plans].sort((a, b) => b.updatedAt - a.updatedAt).map((plan) => (
          <button
            key={plan.id}
            onClick={() => setEditingId(plan.id)}
            style={{
              width: "100%", textAlign: "left", padding: "0.875rem 1rem",
              border: "1px solid hsl(var(--border))", borderRadius: "0.625rem",
              background: "none", cursor: "pointer",
              display: "flex", alignItems: "center", gap: "0.75rem",
            }}
            className="hover:bg-muted transition-colors"
          >
            <Ruler style={{ width: "1.25rem", height: "1.25rem", color: "hsl(var(--primary))", flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: "0.9375rem", color: "hsl(var(--foreground))" }}>{plan.name}</div>
              <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", fontVariantNumeric: "tabular-nums" }}>
                {formatInches(plan.wallWidth)} × {formatInches(plan.wallHeight)} · {plan.studSpacing}" OC
              </div>
            </div>
            <span style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", flexShrink: 0 }}>{fmtDate(plan.updatedAt)}</span>
          </button>
        ))
      )}
    </div>
  );
}
